import { CheckCircle, Question, WarningCircle } from '@phosphor-icons/react';
import { type ReactNode, useEffect, useState } from 'react';
import translations from '../../../translations';
import { type RuntimeNode, RuntimeStatus } from '../../../types/runtime';
import Table, { type TableColumnList } from '../../generic/Table';
import { dateTimerFormatter } from '../utils';

type LocalRuntimeNodes = {
    connectedNodes: RuntimeNode[];
};

const LocalRuntimeNodes = ({ connectedNodes }: LocalRuntimeNodes) => {
    const [nodes, setNodes] = useState<RuntimeNode[]>([]);

    useEffect(() => {
        setNodes(connectedNodes);
    });

    const renderStatusCell = (item: RuntimeNode) => {
        let icon: ReactNode;
        switch (item.status.toLowerCase()) {
            case RuntimeStatus.Offline: {
                icon = (
                    <span
                        data-testid={`online-status-${item.hostname}`}
                        title={translations.LOCAL_RUNTIME_node_offline_status_title_text}
                        className="table-icon-large"
                    >
                        <WarningCircle color="#F4C57F" />
                    </span>
                );
                break;
            }
            case RuntimeStatus.Unknown: {
                icon = (
                    <span
                        data-testid={`online-status-${item.hostname}`}
                        title={translations.LOCAL_RUNTIME_node_offline_status_title_text}
                        className="table-icon-large"
                    >
                        <Question color="#8C8C8C" />
                    </span>
                );
                break;
            }
            case RuntimeStatus.Leader:
            case RuntimeStatus.Online: {
                icon = (
                    <span
                        data-testid={`online-status-${item.hostname}`}
                        title={translations.LOCAL_RUNTIME_node_online_status_title_text}
                        className="table-icon-large"
                    >
                        <CheckCircle color="#0EA076" />
                    </span>
                );
                break;
            }
        }

        return (
            <div className="table-icon-text-wrapper" data-testid="runtime-status">
                {icon}
                <span>{item.status}</span>
            </div>
        );
    };
    const nodeTableColumns: TableColumnList<RuntimeNode> = [
        {
            renderHeader: () => 'Hostname',
            renderCell: ({ item: node }) => node.hostname,
        },
        {
            renderHeader: () => 'Version',
            renderCell: ({ item: node }) => node.version,
        },
        {
            renderHeader: () => 'Last Reported At',
            renderCell: ({ item: node }) => dateTimerFormatter.format(Date.parse(node.reportedAt)),
        },
        {
            renderHeader: () => 'Status',
            renderCell: ({ item: node }) => renderStatusCell(node),
        },
    ];
    const sevenDays = 1000 * 60 * 60 * 24 * 7;
    const nodesToDisplay = nodes.filter(
        (node) => Date.parse(node.reportedAt) > Date.now() - sevenDays,
    );
    return (
        <Table
            caption="Nodes that have reported in online in the last 7 days"
            wrapperClassName="margin-top"
            items={nodesToDisplay}
            columns={nodeTableColumns}
            pagination={true}
            rowKeyGenerator={(node) => node.hostname}
        />
    );
};

export default LocalRuntimeNodes;
